import { useEffect, useRef, useState } from 'react';

/**
 * Copyable identifier field.
 *
 * Motion rule (design/MOTION_AND_MICRO_INTERACTIONS.md):
 * "Copy IBAN/account/reference: label changes to Copied and returns without
 *  layout shift."
 *
 * The button reserves the width of the wider of its two labels up front, so
 * swapping "Copy" for "Copied" cannot reflow the row. The result is also
 * announced politely for screen-reader users, since a visual-only change would
 * not be perceivable.
 */
export function CopyField({
  label,
  value,
  copyValue,
}: {
  label: string;
  /** Formatted for reading. */
  value: string;
  /** Raw value placed on the clipboard. */
  copyValue: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = () => {
    void navigator.clipboard.writeText(copyValue).then(() => {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="tm-copy-field">
      <div className="tm-grow" style={{ minWidth: 0 }}>
        <span className="tm-hint" style={{ display: 'block' }}>
          {label}
        </span>
        <span className="tm-copy-field__value">{value}</span>
      </div>
      <button
        type="button"
        className="tm-copy-btn"
        onClick={copy}
        aria-label={`Copy ${label}`}
      >
        {/* Both labels occupy the same cell, so the control never changes size. */}
        <span className="tm-copy-btn__slot">
          <span aria-hidden="true" data-state={copied ? 'hidden' : 'shown'}>
            Copy
          </span>
          <span aria-hidden="true" data-state={copied ? 'shown' : 'hidden'}>
            Copied
          </span>
        </span>
      </button>
      <span role="status" aria-live="polite" className="tm-visually-hidden">
        {copied ? `${label} copied to clipboard` : ''}
      </span>
    </div>
  );
}
